<template>
  <Table>
    <TableHeader class="bg-grey-light-50 whitespace-nowrap font-medium">
      <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
        <TableHead v-for="header in headerGroup.headers" :key="header.id">
          <FlexRender
            v-if="!header.isPlaceholder"
            :render="header.column.columnDef.header"
            :props="header.getContext()"
          />
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <template v-if="isLoading">
        <TableRow>
          <TableCell :colspan="columns.length" class="h-24 text-center">
            <LayupSpinner :size="10" />
          </TableCell>
        </TableRow>
      </template>
      <template v-else-if="table.getRowModel().rows?.length">
        <TableRow
          v-for="row in table.getRowModel().rows"
          :key="row.id"
          :data-state="row.getIsSelected() ? 'selected' : undefined"
        >
          <TableCell v-for="cell in row.getVisibleCells()" :key="cell.id">
            <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
          </TableCell>
        </TableRow>
      </template>
      <template v-else>
        <TableRow>
          <TableCell :colspan="columns.length" class="h-24 text-center"> No results. </TableCell>
        </TableRow>
      </template>
    </TableBody>
  </Table>

  <div v-if="showPagination" class="my-4 flex justify-center">
    <Pagination v-slot="{ page }" :total="totalCount" :itemsPerPage="pageSize" :page="pageNumber" :default-page="1">
      <PaginationList v-slot="{ items }" class="flex items-center gap-1">
        <PaginationFirst @click="onUpdatePage(1)" />
        <PaginationPrev @click="onUpdatePage(pageNumber - 1)" />

        <template v-for="(item, index) in items">
          <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
            <Button
              class="w-10 h-10 p-0"
              @click="onUpdatePage(item.value)"
              :variant="item.value === page ? 'primary' : 'outline'"
            >
              {{ item.value }}
            </Button>
          </PaginationListItem>
          <PaginationEllipsis v-else :key="item.type" :index="index" />
        </template>

        <PaginationNext @click="onUpdatePage((pageNumber ?? 0) + 1)" />
        <PaginationLast @click="onUpdatePage(totalPages)" />
      </PaginationList>
    </Pagination>
  </div>
</template>

<script setup lang="ts" generic="TData, TValue">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { valueUpdater } from '@/lib/utils';
import type { ColumnDef, SortingState, VisibilityState } from '@tanstack/vue-table';
import { FlexRender, getCoreRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { ref } from 'vue';
import { Button } from '../ui/button';

const props = withDefaults(
  defineProps<{
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    isLoading?: boolean;
    totalPages?: number;
    totalCount?: number;
    pageNumber?: number;
    pageSize?: number;
    showPagination?: boolean;
  }>(),
  {
    isLoading: false,
    totalPages: 1,
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    showPagination: true,
  },
);

const sorting = ref<SortingState>([]);
const columnVisibility = ref<VisibilityState>({});

const emit = defineEmits(['update:page']);
const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return props.columns;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sorting),
  onColumnVisibilityChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnVisibility),
  state: {
    get sorting() {
      return sorting.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
});

const onUpdatePage = (page: number) => {
  emit('update:page', page);
};
</script>
